import { RequestCanceller } from '@shared/utils';
import { StateService } from '@uirouter/angularjs';

import debouncePromise from 'debounce-promise';

import { get } from 'lodash-es';

let requestIdSequence = 0;

export default class Paginator {
    _fetcherService: any;
    _fetcherCallback: any;
    currentParams: any;
    currentCount: number;
    _perPage: number;
    total: number;
    currentPage: number;
    totalPages: number;

    currentCanceller: RequestCanceller;
    requests: any[];
    requestCount: number;
    fetchPage: any;
    _debouncedFetch: any;
    $state: StateService;
    $location: any;
    searchQuery: any;
    $scope: any;

    static get $inject() {
        return ['$util', 'fetcher', 'config'];
    }

    constructor(protected $util, fetcher?, protected config: any = {}) {
        this.$util = $util;
        this._fetcherService = fetcher;
        this._fetcherCallback = config.fetcherCallback || undefined;

        this.currentParams = null;
        this.currentCount = null;
        this._perPage = config.perPage || 20;
        this.total = null;

        this.currentPage = 1;
        this.totalPages = 1;

        this.currentCanceller = new RequestCanceller();
        this.requests = [];
        this.requestCount = 0;

        this.fetchPage = config.fetchPage || undefined;
        this._debouncedFetch = debouncePromise(this._fetch, 50);

        this._uiOnParamsChanged = this._uiOnParamsChanged.bind(this);
    }

    get loading() {
        return this.requestCount === 0 || this.requests.length > 0;
    }

    get fetching() {
        return this.loading;
    }

    get perPage() {
        return this._perPage;
    }

    set perPage(value) {
        this._perPage = value;
    }

    /**
     * Allow a custom fetcher method. Callback will be called with the
     * Fetcher service, as well as any parameters.
     *
     * @param {Function} callback
     */
    set fetcher(callback) {
        this._fetcherCallback = callback;
    }

    /**
     * @param {StateService|LocationHtml5Url} locationService
     * @param $scope
     * @param controller
     * @return {Paginator}
     */
    attach(locationService, $scope, controller) {
        if (locationService instanceof StateService) {
            this.$state = locationService;
        } else {
            this.$location = locationService;
        }

        this.$scope = $scope;

        this.currentPage = this._getQueryParam('page');
        this.searchQuery = this._getQueryParam('q');

        this.$scope.searchQuery = this.searchQuery;

        $scope.$on('$destroy', () => this.cancelCurrentRequest());

        if (controller) {
            controller.uiOnParamsChanged = this._uiOnParamsChanged;
        }

        return this;
    }

    /**
     * Cancel any requests that are currently in-flight.
     */
    cancelCurrentRequest() {
        if (this.currentCanceller) {
            this.currentCanceller.cancel();
        }
    }

    /**
     * @param {number} pageNumber
     * @param {object} params
     * @return {Promise}
     */
    fetch(pageNumber = this.currentPage, params = {}) {
        return this._debouncedFetch(pageNumber, params);
    }

    /**
     * @param {number} pageNumber
     * @param {object} params
     * @return {Promise}
     */
    _fetch(pageNumber = this.currentPage, params: any = {}) {
        this.startRequest();

        params.page = pageNumber;
        params.per_page = this.perPage;

        if (this.searchQuery) {
            params.q = this.searchQuery;
        }

        this.cancelCurrentRequest();

        const promise = this._fetcherCallback
            ? this._fetcherCallback(this._fetcherService, params)
            : this._fetcherService.paginate({
                timeout: this.currentCanceller.start(),
                ...params,
            });

        return promise.then(response => {
            const pagination = response.meta.pagination;

            this.currentParams = params;
            this.currentPage = pagination.current_page;
            this.currentCount = pagination.count;
            this.perPage = pagination.per_page;

            this.totalPages = pagination.total_pages;
            this.total = pagination.total;

            this._updateQueryParameter('page', this.currentPage);

            return response;
        });
    }

    startRequest() {
        const requestId = requestIdSequence++;

        this.requests.push(requestId);
        this.requestCount++;

        return requestId;
    }

    finishRequests() {
        this.requests = [];
    }

    finishRequest(requestId) {
        this.requests.splice(this.requests.indexOf(requestId), 1);
    }

    /**
     * Refresh the current page.
     *
     * @return {Promise}
     */
    refresh() {
        if (this.fetchPage) {
            this.fetchPage(this.currentPage);
        } else {
            throw new Error('fetchPage has not been implemented');
        }
    }

    search(searchQuery) {
        this.searchQuery = searchQuery;

        this._updateQueryParameter('q', this.searchQuery);
    }

    clearSearch() {
        this.searchQuery = null;

        this._updateQueryParameter('q', null);
    }

    /**
     * Handle query parameter changes.
     *
     * @param newParams
     * @private
     */
    _uiOnParamsChanged(newParams: any = {}) {
        const { page } = newParams;

        if (parseInt(page) !== this.currentPage) {
            if (this.fetchPage) {
                this.fetchPage(page);
            }
        }
    }

    _getQueryParam(key) {
        if (this.$state) {
            return get(this.$state, 'params.' + key);
        } else if (this.$location) {
            return this.$location.search()[key];
        }
    }

    _updateQueryParameter(key, value) {
        if (this.$state) {
            const current = this.$state.current;

            const params = {
                ...this.$state.params,
                [key]: value,
            };

            const options = {
                // eslint-disable-next-line eqeqeq
                location: key === 'page' && value == 1 ? 'replace' : true,
            };

            this.$state.go(current.name, params, options);
        } else if (this.$location) {
            this.$location.search(key, value);
        }
    }
}
